import { createFileRoute } from "@tanstack/react-router";
import { FloorFramingGenerator } from "building-generation/elements/structural-framing/floor/floorFraming";
import { FloorFramingTestCases } from "building-generation/elements/structural-framing/floor/floorFramingTestData";
import { RimBoardAlignment, SpanDirection } from "building-types/floors/floorComponentsTypes";
import type {
  FloorFramingInput,
  FloorFramingTestCase,
  GeneratedFramedFloorAssembly as FloorFramingType,
  FloorOpeningFramingConfigGroup,
} from "building-types/framing/floorFramingTypes";
import { LumberProfileType } from "building-types/framing/lumberTypes";
import { closedPolyLine2New } from "geom/polyLine2BrandedTypes";
import { polyLine2New } from "geom/polyLine2Types";
import { useState, useEffect, useCallback, useRef } from "react";
import {
  debugFramingDescriptorFromLumberProfile,
  debugLumberProfileFromDescriptor,
} from "../../../components/debug/floor-framing/components/debugFramingDescriptors";
import { DrawingCanvas } from "../../../components/debug/floor-framing/components/DrawingCanvas";
import type {
  ConstraintLineConfig,
  DrawingState,
  GridConfig,
  Point2D,
} from "../../../components/debug/floor-framing/components/DrawingTypes";
import {
  DEFAULT_ZOOM,
  defaultConstraintLineConfig,
  defaultGridConfig,
  initialDrawingState,
} from "../../../components/debug/floor-framing/components/DrawingTypes";
import { createFloorFramingInputFromDrawing } from "../../../components/debug/floor-framing/components/drawingUtils";
import { FramingParametersPanel } from "../../../components/debug/floor-framing/components/FramingParametersPanel";
import { InputDataEditor } from "../../../components/debug/floor-framing/components/InputDataEditor";
import { SaveLoadControls } from "../../../components/debug/floor-framing/components/SaveLoadControls";
import {
  DRAW_CUSTOM_VALUE,
  TestCaseSelector,
} from "../../../components/debug/floor-framing/components/TestCaseSelector";
import { View3D } from "../../../components/debug/floor-framing/components/View3D";

const MIN_PANEL_WIDTH = 320;
const MAX_PANEL_WIDTH = MIN_PANEL_WIDTH * 2;
const STORAGE_KEY = "floorFramingTestCases";

interface SavedTestCase {
  name: string;
  timestamp: string;
  input: FloorFramingInput;
}

function FloorFramingTestPage() {
  const [panelWidth, setPanelWidth] = useState<number>(MIN_PANEL_WIDTH);
  const [resizing, setResizing] = useState<boolean>(false);
  const [selectedTestCase, setSelectedTestCase] = useState<string>("");
  const [currentInput, setCurrentInput] = useState<FloorFramingInput | null>(null);
  const [generatedFraming, setGeneratedFraming] = useState<FloorFramingType | null>(null);
  const [savedTestCases, setSavedTestCases] = useState<SavedTestCase[]>([]);
  const [spacing, setSpacing] = useState<number>(16);
  const [framingDirection, setFramingDirection] = useState<string>("SHORTEST_SPAN");
  const [framingAngle, setFramingAngle] = useState<number>(0);
  const [useCustomAngle, setUseCustomAngle] = useState<boolean>(false);
  const [defaultLumberProfile, setDefaultLumberProfile] = useState<LumberProfileType>(LumberProfileType.SPF_2X8);
  const [rimBoardAlignment, setRimBoardAlignment] = useState<RimBoardAlignment>(RimBoardAlignment.INTERIOR);
  const [framingStartPtX, setFramingStartPtX] = useState<number>(0);
  const [framingStartPtY, setFramingStartPtY] = useState<number>(0);
  const [currentView, setCurrentView] = useState<"XY" | "XZ" | "YZ" | "ISO">("ISO");
  const [generationTimeMs, setGenerationTimeMs] = useState<number | null>(null);
  const [openingConfig, setOpeningConfig] = useState<FloorOpeningFramingConfigGroup | undefined>(undefined);
  const [forceTrimmersAtOpeningEnds, setForceTrimmersAtOpeningEnds] = useState<boolean>(false);

  // Drawing mode state
  const [isDrawingMode, setIsDrawingMode] = useState<boolean>(false);
  const [showDrawingView, setShowDrawingView] = useState<boolean>(true);
  const [drawingState, setDrawingState] = useState<DrawingState>(initialDrawingState);
  const [drawingGridConfig, setDrawingGridConfig] = useState<GridConfig>(defaultGridConfig);
  const [drawingConstraintLineConfig, setDrawingConstraintLineConfig] =
    useState<ConstraintLineConfig>(defaultConstraintLineConfig);
  const [drawingZoom, setDrawingZoom] = useState<number>(DEFAULT_ZOOM);

  // Angle animation state
  const [angleStep, setAngleStep] = useState<number>(5);
  const [animationDelay, setAnimationDelay] = useState<number>(100);
  const [isAnimating, setIsAnimating] = useState<boolean>(false);
  const animationFrameRef = useRef<number | null>(null);
  const lastAnimationTimeRef = useRef<number>(0);
  const isGeneratingRef = useRef<boolean>(false);

  // Load saved test cases from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSavedTestCases(Array.isArray(parsed) ? parsed : []);
      } catch (e) {
        console.error("Failed to load saved test cases:", e);
      }
    }
  }, []);

  // Handle mouse move for resizing
  useEffect(() => {
    if (!resizing) {
      return;
    }
    const handleMouseMove = (e: MouseEvent) => {
      setPanelWidth(() => Math.max(MIN_PANEL_WIDTH, Math.min(MAX_PANEL_WIDTH, e.clientX)));
    };
    const handleMouseUp = () => setResizing(false);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [resizing]);

  // Generate framing with performance timing
  const generateFraming = useCallback((input: FloorFramingInput, alignment: RimBoardAlignment) => {
    // Skip if already generating (prevents backlog during animation)
    if (isGeneratingRef.current) {
      return;
    }
    isGeneratingRef.current = true;
    try {
      const startTime = performance.now();
      // The debug page operates without a real doc, so we pass an empty framing
      // components table; the rim defaults to the joist via the `defaultFramingDescriptor`.
      const floorFraming = new FloorFramingGenerator(input, {}, 0, undefined, alignment);
      const result = floorFraming.generate();
      const endTime = performance.now();
      setGenerationTimeMs(endTime - startTime);
      setGeneratedFraming(result);
    } catch (error) {
      console.error("Failed to generate framing:", error);
      setGeneratedFraming(null);
      setGenerationTimeMs(null);
    } finally {
      isGeneratingRef.current = false;
    }
  }, []);

  // Auto-generate framing whenever currentInput changes
  useEffect(() => {
    if (currentInput) {
      // Merge current input with latest framing parameters from UI state
      // Convert degrees to radians when using custom angle
      const directionValue = useCustomAngle ? (framingAngle * Math.PI) / 180 : (framingDirection as SpanDirection);

      const inputWithLatestParams: FloorFramingInput = {
        ...currentInput,
        framingMetadata: {
          ...currentInput.framingMetadata,
          spacing,
          direction: directionValue,
          defaultFramingDescriptor: debugFramingDescriptorFromLumberProfile(defaultLumberProfile),
          framingStartPt: [framingStartPtX, framingStartPtY],
          forceTrimmersAtOpeningEnds,
        },
        openingFramingConfigGroup: openingConfig,
      };
      generateFraming(inputWithLatestParams, rimBoardAlignment);
    }
  }, [
    currentInput,
    spacing,
    framingDirection,
    framingAngle,
    useCustomAngle,
    defaultLumberProfile,
    framingStartPtX,
    framingStartPtY,
    openingConfig,
    forceTrimmersAtOpeningEnds,
    rimBoardAlignment,
    generateFraming,
  ]);

  // Angle animation effect using requestAnimationFrame for better performance
  useEffect(() => {
    if (!isAnimating || !useCustomAngle) {
      // Cancel any pending animation frame
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      return;
    }

    const animate = (currentTime: number) => {
      // Check if enough time has passed since last update
      const elapsed = currentTime - lastAnimationTimeRef.current;

      if (elapsed >= animationDelay) {
        // Only update if not currently generating (skip frame if busy)
        if (!isGeneratingRef.current) {
          setFramingAngle((prevAngle) => {
            // Wrap around at 360 degrees
            return (prevAngle + angleStep) % 360;
          });
          lastAnimationTimeRef.current = currentTime;
        }
      }

      // Schedule next frame
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Start animation loop
    lastAnimationTimeRef.current = performance.now();
    animationFrameRef.current = requestAnimationFrame(animate);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isAnimating, useCustomAngle, angleStep, animationDelay]);

  // Stop animation when switching away from custom angle
  useEffect(() => {
    if (!useCustomAngle && isAnimating) {
      setIsAnimating(false);
    }
  }, [useCustomAngle, isAnimating]);

  // Reset framing direction to a valid SpanDirection when switching away from custom angle
  useEffect(() => {
    if (!useCustomAngle && framingDirection === "") {
      setFramingDirection(SpanDirection.SHORTEST_SPAN);
    }
  }, [useCustomAngle, framingDirection]);

  // Load test case with deep cloning to preserve original
  const loadTestCase = useCallback((testCase: FloorFramingTestCase) => {
    // Deep clone the entire input to preserve original test case
    const clonedInput: FloorFramingInput = {
      ...JSON.parse(JSON.stringify(testCase.input)),
      framingMetadata: {
        ...JSON.parse(JSON.stringify(testCase.input.framingMetadata)),
        framingStartPt: testCase.input.framingMetadata.framingStartPt ?? [0, 0],
      },
      perimeter: {
        ...JSON.parse(JSON.stringify(testCase.input.perimeter)),
        polyline: closedPolyLine2New(polyLine2New([...testCase.input.perimeter.polyline])),
        vertexMetadata: testCase.input.perimeter.vertexMetadata
          ? { ...testCase.input.perimeter.vertexMetadata }
          : undefined,
        edgeMetadata: testCase.input.perimeter.edgeMetadata ? { ...testCase.input.perimeter.edgeMetadata } : undefined,
      },
      openings: testCase.input.openings
        ? testCase.input.openings.map((opening) => ({
            ...opening,
            polyline: closedPolyLine2New(polyLine2New([...opening.polyline])),
            vertexMetadata: opening.vertexMetadata ? { ...opening.vertexMetadata } : undefined,
            edgeMetadata: opening.edgeMetadata ? { ...opening.edgeMetadata } : undefined,
          }))
        : undefined,
      constraintLines: testCase.input.constraintLines
        ? testCase.input.constraintLines.map((constraintLine) => ({
            ...constraintLine,
            polyline: polyLine2New([...constraintLine.polyline]),
          }))
        : undefined,
    };

    setCurrentInput(clonedInput);
    setSpacing(clonedInput.framingMetadata.spacing);
    if (typeof clonedInput.framingMetadata.direction === "number") {
      // Convert radians to degrees for UI display
      setFramingAngle((clonedInput.framingMetadata.direction * 180) / Math.PI);
      setUseCustomAngle(true);
      setFramingDirection("");
    } else {
      setUseCustomAngle(false);
      setFramingDirection(clonedInput.framingMetadata.direction);
    }
    setDefaultLumberProfile(
      debugLumberProfileFromDescriptor(clonedInput.framingMetadata.defaultFramingDescriptor) ??
        LumberProfileType.SPF_2X10,
    );
    // Load framing start point
    const startPt = clonedInput.framingMetadata.framingStartPt ?? [0, 0];
    setFramingStartPtX(startPt[0]);
    setFramingStartPtY(startPt[1]);
    setForceTrimmersAtOpeningEnds(clonedInput.framingMetadata.forceTrimmersAtOpeningEnds ?? false);
    setRimBoardAlignment(RimBoardAlignment.INTERIOR);
    // Load opening framing config
    setOpeningConfig(testCase.input.openingFramingConfigGroup);
  }, []);

  // Don't auto-regenerate - user must click "Generate Framing" button

  // Save current test case
  const saveTestCase = useCallback(() => {
    if (!currentInput) {
      return;
    }
    const saved: SavedTestCase = {
      name: `Custom Test Case ${savedTestCases.length + 1}`,
      timestamp: new Date().toISOString(),
      input: currentInput,
    };
    const updated = [...savedTestCases, saved];
    setSavedTestCases(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  }, [currentInput, savedTestCases]);

  // Load saved test case
  const loadSavedTestCase = useCallback(
    (saved: SavedTestCase) => {
      loadTestCase({ name: saved.name, description: saved.timestamp, input: saved.input });
    },
    [loadTestCase],
  );

  // Delete saved test case
  const deleteSavedTestCase = useCallback(
    (index: number) => {
      const updated = savedTestCases.filter((_, i) => i !== index);
      setSavedTestCases(updated);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    },
    [savedTestCases],
  );

  // Export to JSON file
  const exportToFile = useCallback(() => {
    if (!currentInput) {
      return;
    }
    const data: SavedTestCase = {
      name: "Exported Test Case",
      timestamp: new Date().toISOString(),
      input: currentInput,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "floor-framing-test-case.json";
    a.click();
    URL.revokeObjectURL(url);
  }, [currentInput]);

  // Load from JSON file
  const loadFromFile = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) {
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const parsed = JSON.parse(content) as SavedTestCase;
          if (parsed.input) {
            loadSavedTestCase(parsed);
          }
        } catch (error) {
          console.error("Failed to load file:", error);
          alert("Failed to load test case from file");
        }
      };
      reader.readAsText(file);
    },
    [loadSavedTestCase],
  );

  // Handle drawing changes from DrawingCanvas
  const handleDrawingChange = useCallback(
    (
      perimeter: Point2D[] | null,
      openings: Point2D[][],
      supportLines: Point2D[][] = [],
      framingLines: Point2D[][] = [],
    ) => {
      // Generate FloorFramingInput from drawn polygons and constraint lines
      if (perimeter && perimeter.length >= 3) {
        const input = createFloorFramingInputFromDrawing(
          perimeter,
          openings,
          supportLines,
          framingLines,
          drawingConstraintLineConfig.supportLineWidth,
          drawingConstraintLineConfig.framingLineWidth,
        );
        setCurrentInput(input);
      } else {
        setCurrentInput(null);
        setGeneratedFraming(null);
      }
    },
    [drawingConstraintLineConfig],
  );

  // Handle test case selection change
  const handleTestCaseChange = useCallback(
    (name: string) => {
      setSelectedTestCase(name);
      setGeneratedFraming(null);

      if (name === DRAW_CUSTOM_VALUE) {
        // Enter drawing mode - reset drawing state but keep settings (zoom, grid)
        setIsDrawingMode(true);
        setShowDrawingView(true);
        setDrawingState(initialDrawingState);
        setCurrentInput(null);
      } else if (name) {
        // Load a predefined test case
        setIsDrawingMode(false);
        const testCase = FloorFramingTestCases.find((tc) => tc.name === name);
        if (testCase) {
          loadTestCase(testCase);
        }
      } else {
        // No selection
        setIsDrawingMode(false);
        setCurrentInput(null);
      }
    },
    [loadTestCase],
  );

  return (
    <div
      style={{
        display: "flex",
        height: "100vh",
        fontFamily: "Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif",
      }}
    >
      {/* Left Panel: UI Controls */}
      <div
        style={{
          width: panelWidth,
          minWidth: MIN_PANEL_WIDTH,
          maxWidth: MAX_PANEL_WIDTH,
          padding: 8,
          borderRight: "1px solid #eee",
          boxSizing: "border-box",
          height: "100vh",
          overflowY: "auto",
          fontFamily: "inherit",
        }}
      >
        <h2>FLOOR FRAMING TEST</h2>

        <TestCaseSelector
          selectedTestCase={selectedTestCase}
          onTestCaseChange={handleTestCaseChange}
          currentInput={currentInput}
          isDrawingMode={isDrawingMode}
        />

        <FramingParametersPanel
          spacing={spacing}
          onSpacingChange={setSpacing}
          framingDirection={framingDirection}
          onFramingDirectionChange={setFramingDirection}
          framingAngle={framingAngle}
          onFramingAngleChange={setFramingAngle}
          useCustomAngle={useCustomAngle}
          onUseCustomAngleChange={setUseCustomAngle}
          defaultLumberProfile={defaultLumberProfile}
          onDefaultLumberProfileChange={setDefaultLumberProfile}
          rimBoardAlignment={rimBoardAlignment}
          onRimBoardAlignmentChange={setRimBoardAlignment}
          framingStartPtX={framingStartPtX}
          framingStartPtY={framingStartPtY}
          onFramingStartPtXChange={setFramingStartPtX}
          onFramingStartPtYChange={setFramingStartPtY}
          forceTrimmersAtOpeningEnds={forceTrimmersAtOpeningEnds}
          onForceTrimmersAtOpeningEndsChange={setForceTrimmersAtOpeningEnds}
          hasOpenings={Boolean(currentInput?.openings && currentInput.openings.length > 0)}
          openingConfig={openingConfig}
          onOpeningConfigChange={setOpeningConfig}
          angleStep={angleStep}
          onAngleStepChange={setAngleStep}
          animationDelay={animationDelay}
          onAnimationDelayChange={setAnimationDelay}
          isAnimating={isAnimating}
          onToggleAnimation={() => setIsAnimating((prev) => !prev)}
        />

        <SaveLoadControls
          currentInput={currentInput}
          savedTestCases={savedTestCases}
          onSave={saveTestCase}
          onExport={exportToFile}
          onLoadFromFile={loadFromFile}
          onLoadSaved={loadSavedTestCase}
          onDeleteSaved={deleteSavedTestCase}
        />

        <InputDataEditor currentInput={currentInput} onInputChange={setCurrentInput} />
      </div>

      {/* Resizer */}
      <div
        style={{
          width: 8,
          cursor: "col-resize",
          background: resizing ? "#bdbdbd" : "#e0e0e0",
          zIndex: 100,
          userSelect: "none",
          height: "100vh",
          boxShadow: "0 0 4px #bbb",
          transition: "background 0.2s",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onMouseDown={() => setResizing(true)}
        title="Drag to resize panel"
      >
        <div
          style={{
            width: 2,
            height: "60%",
            background: "#888",
            borderRadius: 2,
            opacity: 0.5,
          }}
        />
      </div>

      {/* Right Panel: Drawing Canvas or 3D Viewer */}
      <div style={{ flex: 1, position: "relative", background: "#fafbfc", fontFamily: "inherit" }}>
        {isDrawingMode && showDrawingView ? (
          <DrawingCanvas
            onDrawingChange={handleDrawingChange}
            panelWidth={panelWidth}
            onToggleView={() => setShowDrawingView(false)}
            hasFraming={generatedFraming !== null}
            drawingState={drawingState}
            onDrawingStateChange={setDrawingState}
            gridConfig={drawingGridConfig}
            onGridConfigChange={setDrawingGridConfig}
            constraintLineConfig={drawingConstraintLineConfig}
            onConstraintLineConfigChange={setDrawingConstraintLineConfig}
            zoom={drawingZoom}
            onZoomChange={setDrawingZoom}
          />
        ) : (
          <View3D
            panelWidth={panelWidth}
            currentInput={currentInput}
            generatedFraming={generatedFraming}
            currentView={currentView}
            onViewChange={setCurrentView}
          />
        )}

        {/* Toggle button to switch back to drawing view */}
        {isDrawingMode && !showDrawingView && (
          <button
            onClick={() => setShowDrawingView(true)}
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              padding: "8px 16px",
              background: "#2196F3",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              cursor: "pointer",
              fontWeight: 500,
              fontSize: 14,
              zIndex: 20,
              boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
            }}
          >
            Back to Drawing
          </button>
        )}

        {/* Performance timing display */}
        {generationTimeMs !== null && (
          <div
            style={{
              position: "absolute",
              bottom: 16,
              right: 16,
              background: "rgba(255, 255, 255, 0.9)",
              border: "1px solid #ccc",
              borderRadius: 4,
              padding: "6px 12px",
              fontSize: 12,
              fontFamily: "monospace",
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              zIndex: 10,
            }}
          >
            Generation: <strong>{generationTimeMs.toFixed(2)} ms</strong>
          </div>
        )}
      </div>
    </div>
  );
}

// View3D component has been extracted to ./components/View3D.tsx

export const Route = createFileRoute("/debug/floor-framing/")({
  component: FloorFramingTestPage,
});
